import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  addNotification,
  moveCategory,
  moveCategoryGroup,
} from 'loot-core/client/actions';
import { send } from 'loot-core/platform/client/fetch';

import { useCategories } from '../../hooks/useCategories';
import { useResponsive } from '../../ResponsiveProvider';
import { theme } from '../../style';
import { findSortDown, findSortUp } from '../budget/util';
import { Button } from '../common/Button';
import { Search } from '../common/Search';
import { View } from '../common/View';
import { MobilePageHeader, Page } from '../Page';
import { usePageTitle } from '../PageTitleContext';
import { DropHighlightPosContext } from '../sort';

import ManageCategoriesCategoryGroup from './ManageCategoriesCategoryGroup';
import { UndoRedoButtons } from '../common/UndoRedoButtons';

function EmptyMessage({ text, style }) {
  return (
    <View
      style={{
        textAlign: 'center',
        color: theme.pageTextSubdued,
        fontStyle: 'italic',
        fontSize: 13,
        marginTop: 5,
        ...style,
      }}
    >
      {text}
    </View>
  );
}

const ManageCategoriesPage: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const { grouped: categoryGroups, list: categoriesList } = useCategories();
  const { isNarrowWidth } = useResponsive();
  const dispatch = useDispatch();

  const [filter, setFilter] = useState('');
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [newCategoryForGroup, setNewCategoryForGroup] = useState(null);
  const [dragState, setDragState] = useState(null);

  useEffect(() => {
    setPageTitle('Categories');
  }, []);

  const filteredCategories = useMemo(() => {
    let categoriesKeyValuePairs = categoriesList.map(cat => [cat.id, cat]);
    if (filter) {
      categoriesKeyValuePairs = categoriesList
        .filter(p => p.name.toLowerCase().includes(filter.toLowerCase()))
        .map(cat => [cat.id, cat]);
    }
    return new Map(categoriesKeyValuePairs);
  }, [categoriesList, filter]);

  const { incomeGroup, expenseGroups, lastCategoryId } = useMemo(() => {
    let incomeGroup = null;
    let expenseGroups = null;
    let lastCategoryId = null;
    if (categoryGroups && categoryGroups.length) {
      const allGroups = structuredClone(categoryGroups);
      const incomeGroupIndex = allGroups.findIndex(g => g.is_income);
      if (incomeGroupIndex !== -1) {
        incomeGroup = allGroups[incomeGroupIndex];
        incomeGroup.categories = incomeGroup.categories.filter(cat =>
          filteredCategories.has(cat.id),
        );
        if (incomeGroup.id === newCategoryForGroup) {
          incomeGroup.categories = [
            {
              id: 'new',
              cat_group: newCategoryForGroup,
              name: '',
              is_income: true,
              is_donation: false,
            },
          ].concat(incomeGroup.categories);
        }
        allGroups.splice(incomeGroupIndex, 1);
      }
      allGroups.forEach((g, idx) => {
        g.categories = g.categories.filter(cat =>
          filteredCategories.has(cat.id),
        );
        if (
          idx === allGroups.length - 1 &&
          !g.is_donation &&
          !g.is_income &&
          g.categories.length
        ) {
          lastCategoryId = g.categories.at(-1).id;
        }
        if (g.id === newCategoryForGroup) {
          g.categories = [
            {
              id: 'new',
              name: '',
              cat_group: newCategoryForGroup,
              is_income: false,
            },
          ].concat(g.categories);
        }
      });
      expenseGroups = allGroups;
    }
    return {
      incomeGroup,
      expenseGroups,
      lastCategoryId,
    };
  }, [categoryGroups, filteredCategories, newCategoryForGroup]);

  const filterChangeHandler = (value: string) => {
    setFilter(value);
  };

  function onShowNewGroup() {
    setIsAddingGroup(true);
  }

  function onHideNewGroup() {
    setIsAddingGroup(false);
  }

  function onShowNewCategory(groupId) {
    setNewCategoryForGroup(groupId);
  }

  function onHideNewCategory() {
    setNewCategoryForGroup(null);
  }

  function onDragChange(newDragState) {
    const { state } = newDragState;

    if (state === 'start-preview') {
      setDragState({
        type: newDragState.type,
        item: newDragState.item,
        preview: true,
      });
    } else if (state === 'start') {
      if (dragState) {
        setDragState({
          ...dragState,
          preview: false,
        });
      }
    } else if (state === 'hover') {
      setDragState({
        ...dragState,
        hoveredId: newDragState.id,
        hoveredPos: newDragState.pos,
      });
    } else if (state === 'end') {
      setDragState(null);
    }
  }

  const categoryNameAlreadyExistsNotification = name => {
    dispatch(
      addNotification({
        type: 'error',
        message: `Category ‘${name}’ already exists in group`,
      }),
    );
  };

  const onReorderCategory = async sortInfo => {
    const cats = await send('get-categories');
    const moveCandidate = cats.list.filter(c => c.id === sortInfo.id)[0];
    const exists =
      cats.grouped
        .filter(g => g.id === sortInfo.groupId)[0]
        .categories.filter(
          c => c.name.toUpperCase() === moveCandidate.name.toUpperCase(),
        )
        .filter(c => c.id !== moveCandidate.id).length > 0;

    if (exists) {
      categoryNameAlreadyExistsNotification(moveCandidate.name);
      return;
    }

    dispatch(moveCategory(sortInfo.id, sortInfo.groupId, sortInfo.targetId));
  };

  const onReorderGroup = async sortInfo => {
    dispatch(moveCategoryGroup(sortInfo.id, sortInfo.targetId));
  };

  const _onReorderCategory = (id, dropPos, targetId) => {
    const isGroup = !!categoryGroups.find(g => g.id === targetId);

    if (isGroup) {
      const { targetId: groupId } = findSortUp(
        categoryGroups,
        dropPos,
        targetId,
      );
      const group = categoryGroups.find(g => g.id === groupId);

      if (group) {
        const { categories } = group;
        onReorderCategory({
          id,
          groupId: group.id,
          targetId:
            categories.length === 0 || dropPos === 'top'
              ? null
              : categories[0].id,
        });
      }
    } else {
      let targetGroup;

      for (const group of categoryGroups) {
        if (group.categories.find(cat => cat.id === targetId)) {
          targetGroup = group;
          break;
        }
      }

      onReorderCategory({
        id,
        groupId: targetGroup.id,
        ...findSortDown(targetGroup.categories, dropPos, targetId),
      });
    }
  };

  const _onReorderGroup = (id, dropPos, targetId) => {
    onReorderGroup({
      id,
      ...findSortDown(categoryGroups, dropPos, targetId),
    });
  };

  return (
    <Page
      header={
        isNarrowWidth ? (
          <MobilePageHeader style={{ marginBottom: 12 }} title="Categories" />
        ) : null
      }
      style={{ minHeight: 'auto', height: 'fit-Content' }}
      childrenWrapperStyles={{
        minHeight: 'auto',
        paddingBottom: 40,
      }}
    >
      <View style={{ height: 'fit-content' }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            padding: '3px 0 15px',
          }}
        >
          <View>
            <UndoRedoButtons />
          </View>
          {!isNarrowWidth && <View style={{ flex: 1 }} />}
          <Search
            placeholder="Filter categories..."
            value={filter}
            onChange={filterChangeHandler}
            style={{ marginLeft: isNarrowWidth ? 16 : 0 }}
            {...(isNarrowWidth ? { width: '100%' } : {})}
          />
        </View>
        <View
          style={{
            flex: 1,
            border: '1px solid ' + theme.tableBorder,
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          {filteredCategories.size === 0 ? (
            <EmptyMessage
              text="No categories"
              style={{ marginTop: 15, marginBottom: 15 }}
            />
          ) : (
            <>
              <ManageCategoriesCategoryGroup
                categoryGroup={incomeGroup}
                categoryGroups={categoryGroups}
                onShowNewCategory={onShowNewCategory}
                onHideNewCategory={onHideNewCategory}
                onDragChange={onDragChange}
                onReorderCategory={_onReorderCategory}
                onReorderGroup={_onReorderGroup}
                activeFilter={!!filter}
                dragState={dragState}
              />
              {expenseGroups.some(g => g.categories.length) ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 20px',
                      height: 32,
                      backgroundColor:
                        theme.tableRowHeaderAlternativeBackground,
                      fontWeight: 500,
                    }}
                  >
                    Expenses
                  </div>
                  {expenseGroups
                    .concat(isAddingGroup ? [{ id: 'new', name: '' }] : [])
                    .map((group, idx) => (
                      <DropHighlightPosContext.Provider
                        key={group.id || idx}
                        value={
                          idx === 0
                            ? 'first'
                            : idx === expenseGroups.length - 1 &&
                                !lastCategoryId
                              ? 'last'
                              : null
                        }
                      >
                        <View
                          style={
                            !dragState && {
                              ':hover': {
                                backgroundColor: theme.tableBackground,
                              },
                              ...(group.is_donation
                                ? { marginBottom: 10 }
                                : {}),
                            }
                          }
                        >
                          <ManageCategoriesCategoryGroup
                            key={group.id}
                            categoryGroup={group}
                            indent={true}
                            categoryGroups={categoryGroups}
                            onHideNewGroup={onHideNewGroup}
                            onShowNewCategory={onShowNewCategory}
                            onHideNewCategory={onHideNewCategory}
                            activeFilter={!!filter}
                            dragState={dragState}
                            onDragChange={onDragChange}
                            onReorderCategory={_onReorderCategory}
                            onReorderGroup={_onReorderGroup}
                            lastCategoryId={lastCategoryId}
                          />
                        </View>
                      </DropHighlightPosContext.Provider>
                    ))}
                </>
              ) : null}
              <View style={{ flexDirection: 'row', flex: 1 }}>
                <View
                  style={{
                    width: '100%',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    flexDirection: 'row',
                    paddingLeft: 20,
                    backgroundColor: theme.tableBackground,
                    borderTop: `1px solid ${theme.tableBorder}`,
                  }}
                >
                  <Button
                    onClick={onShowNewGroup}
                    style={{ fontSize: 12, margin: 10 }}
                  >
                    Add Group
                  </Button>
                </View>
              </View>
            </>
          )}
        </View>
      </View>
    </Page>
  );
};
export default ManageCategoriesPage;
